import { ActionIcon, Menu } from "@mantine/core";
import { useNavigate } from "@tanstack/react-router";
import { Gift } from "lucide-react";
import { useState } from "react";

import { useGetAppearance } from "../../api/generated/endpoints/appearance/appearance";
import type { UnseenAward } from "../../api/generated/model";
import { ROOM_BELOW } from "../../ui/popover-room";
import { useBoard, useMarkSeen } from "../achievements/board";
import { AchievementsEntry } from "../achievements/GiftEntry";
import { ErrorAlert } from "../ErrorAlert";
import classes from "./case.module.css";
import { CaseDialog } from "./CaseDialog";
import { useCaseDay } from "./daily-case";

/**
 * The header's very small gift icon, beside the sun or moon (Daniil, 2026-10-28), and its menu: one
 * `Menu.Item` per entry, the free daily case and achievements (#1207; spec web/achievements, 3). The
 * icon is drawn while an entry has something to show: the case unless the person or the team hides
 * it or every theme is owned, achievements unless the person hides them. Its dot
 * (`data-attention`), the case's own signal, shows while the case waits or unlocks are unseen; their
 * count is in the menu and the icon's name, never on the icon (Arturas, spec web/achievements,
 * decision 12). `data-gift-icon` is the stable hook for what flies into the icon: achievements'
 * unlock card.
 */
export function GiftMenu({ teamId }: { teamId: string }) {
  const view = useGetAppearance(teamId).data;
  const board = useBoard().data;
  const refreshIfOver = useCaseDay(view?.case.next_at);
  const [dialog, setDialog] = useState(false);
  // The unlocks the menu opened with: marked seen as it opens, listed until it closes.
  const [listed, setListed] = useState<readonly UnseenAward[]>([]);
  const { mark: markSeen, error: seenError } = useMarkSeen();
  const navigate = useNavigate();
  const cases = view !== undefined && view.case.status !== "hidden" && view.case.status !== "none" ? view : undefined;
  const achievements = board !== undefined && !board.hidden ? board : undefined;
  if (cases === undefined && achievements === undefined) return null;
  const waiting = cases?.case.status === "waiting";
  const unseen = achievements?.unseen.length ?? 0;
  const news = [
    ...(waiting ? ["your free case is ready"] : []),
    ...(unseen > 0 ? [`${unseen} new ${unseen === 1 ? "achievement" : "achievements"}`] : []),
  ];
  const opened = () => {
    refreshIfOver();
    const shown = achievements?.unseen ?? [];
    setListed(shown);
    // A refusal, this opening's or another marking's, shows in the menu until the next list's marks end.
    if (shown.length > 0) markSeen(shown.map((award) => award.award_id));
  };
  return (
    <>
      {/* The room under the icon bounds the menu, which scrolls in it (spec web/achievements, decision 21). */}
      <Menu position="bottom-end" offset={6} onOpen={opened} middlewares={ROOM_BELOW} classNames={{ dropdown: classes.menu }}>
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            size="lg"
            radius="md"
            className={classes.gift}
            data-gift-icon
            data-attention={waiting || unseen > 0 || undefined}
            aria-label={news.length === 0 ? "Gifts" : `Gifts: ${news.join(", ")}`}
          >
            <Gift size={18} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {cases === undefined ? null : (
            <Menu.Item leftSection={<Gift size={16} />} onClick={() => setDialog(true)}>
              {waiting ? "Open your free daily case" : "Today's free case"}
            </Menu.Item>
          )}
          {achievements === undefined ? null : (
            <>
              <AchievementsEntry
                board={achievements}
                listed={listed}
                onOpenTab={() => void navigate({ to: "/teams/$teamId/account/achievements", params: { teamId } })}
              />
              {seenError === null ? null : <ErrorAlert error={seenError} />}
            </>
          )}
        </Menu.Dropdown>
      </Menu>
      {/* One dialog a day: a new day's case starts with no draw from the day before. */}
      {cases === undefined ? null : (
        <CaseDialog key={cases.case.day} teamId={teamId} view={cases} opened={dialog} onClose={() => setDialog(false)} />
      )}
    </>
  );
}
